import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Auto Researcher nodes - showing evolution of research/analysis code
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisAutoResearcherPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // Research Question
    {
      id: 'research-question',
      data: { label: '🔬 "Analyze customer behavior patterns and predict churn risk"' },
      position: { x: 400, y: 50 },
      style: themeStyles.problemNode,
    },
    
    // Data Context
    {
      id: 'data-context',
      data: { label: '📋 Available Data\n• Customer transactions\n• Support tickets\n• Product usage logs\n• Demographics' },
      position: { x: 800, y: 50 },
      style: { ...themeStyles.analysisNode, width: 200 },
    },
    
    // Initial Analysis
    {
      id: 'initial-analysis',
      type: 'collapsibleCode',
      data: { 
        filename: 'analysis_v1.py',
        code: `import pandas as pd
import numpy as np

# Basic analysis
df = pd.read_csv("customer_data.csv")

# Simple churn calculation
churn_rate = df['churned'].mean()
print(f"Churn rate: {churn_rate:.2%}")

# Basic correlation
corr_matrix = df.corr()
print(corr_matrix['churned'].sort_values())`,
        issues: 'Superficial analysis, no actionable insights, limited scope',
        score: 2.3
      },
      position: { x: 400, y: 200 },
    },
    
    // Research Quality Scoring
    {
      id: 'research-scoring',
      data: { label: '🔬 Research Quality Metrics' },
      position: { x: 850, y: 200 },
      style: { ...themeStyles.metricNode, width: 200 },
    },
    {
      id: 'insight-depth',
      data: { label: '🔍 Insight Depth\nNovel Findings' },
      position: { x: 750, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'statistical-rigor',
      data: { label: '📊 Statistical Rigor\nMethodology' },
      position: { x: 900, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'actionability',
      data: { label: '🎯 Actionability\nPractical Value' },
      position: { x: 1050, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'comprehensiveness',
      data: { label: '📚 Comprehensiveness\nCoverage' },
      position: { x: 825, y: 360 },
      style: themeStyles.metricNode,
    },
    {
      id: 'clarity',
      data: { label: '✨ Clarity\nInterpretability' },
      position: { x: 975, y: 360 },
      style: themeStyles.metricNode,
    },
    
    // Research Agents
    {
      id: 'research-agents',
      data: { label: '🤖 Specialized Research Agents' },
      position: { x: 400, y: 450 },
      style: { ...themeStyles.analysisNode, width: 200 },
    },
    {
      id: 'statistician',
      data: { label: '📈 Statistician\nAgent' },
      position: { x: 200, y: 530 },
      style: themeStyles.llmNode,
    },
    {
      id: 'data-scientist',
      data: { label: '🧪 Data Scientist\nAgent' },
      position: { x: 400, y: 530 },
      style: themeStyles.llmNode,
    },
    {
      id: 'domain-expert',
      data: { label: '💼 Domain Expert\nAgent' },
      position: { x: 600, y: 530 },
      style: themeStyles.llmNode,
    },
    
    // Generation 1
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Enhanced Analysis' },
      position: { x: 400, y: 630 },
      style: { ...themeStyles.metricNode, width: 250 },
    },
    {
      id: 'gen1-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'analysis_gen1_segmentation.py',
        code: `import pandas as pd
from sklearn.preprocessing import StandardScaler
from sklearn.cluster import KMeans
import seaborn as sns

# Advanced customer segmentation
scaler = StandardScaler()
features = ['recency', 'frequency', 'monetary', 'support_tickets']
X_scaled = scaler.fit_transform(df[features])

# Optimal clusters using elbow method
inertias = []
for k in range(2, 10):
    kmeans = KMeans(n_clusters=k, random_state=42)
    kmeans.fit(X_scaled)
    inertias.append(kmeans.inertia_)

# Segment customers
optimal_k = 4
kmeans = KMeans(n_clusters=optimal_k)
df['segment'] = kmeans.fit_predict(X_scaled)

# Analyze churn by segment
segment_analysis = df.groupby('segment').agg({
    'churned': ['mean', 'count'],
    'lifetime_value': 'mean',
    'support_tickets': 'mean'
})

print("High-risk segments identified:")
print(segment_analysis[segment_analysis['churned']['mean'] > 0.3])`,
        score: 3.7
      },
      position: { x: 50, y: 700 },
    },
    {
      id: 'gen1-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'analysis_gen1_predictive.py',
        code: `import pandas as pd
from sklearn.ensemble import RandomForestClassifier
from sklearn.model_selection import cross_val_score
import shap

# Feature engineering
df['days_since_purchase'] = (pd.Timestamp.now() - df['last_purchase']).dt.days
df['support_per_month'] = df['support_tickets'] / df['months_active']
df['revenue_trend'] = df.groupby('customer_id')['monthly_spend'].pct_change()

# Predictive model
features = ['days_since_purchase', 'support_per_month', 'revenue_trend', 
            'product_usage_score', 'satisfaction_score']
X = df[features]
y = df['churned']

# Train with interpretability
rf = RandomForestClassifier(n_estimators=100, random_state=42)
scores = cross_val_score(rf, X, y, cv=5, scoring='roc_auc')
print(f"Model AUC: {scores.mean():.3f} (+/- {scores.std()*2:.3f})")

# SHAP analysis for insights
rf.fit(X, y)
explainer = shap.TreeExplainer(rf)
shap_values = explainer.shap_values(X)

# Key insights
print("\n🔍 Key Churn Drivers:")
for i, feature in enumerate(features):
    impact = abs(shap_values[1][:, i]).mean()
    print(f"- {feature}: {impact:.3f} impact")`,
        score: 4.3
      },
      position: { x: 400, y: 700 },
    },
    {
      id: 'gen1-3',
      type: 'collapsibleCode',
      data: { 
        filename: 'analysis_gen1_behavioral.py',
        code: `import pandas as pd
import networkx as nx
from scipy import stats

# Behavioral pattern analysis
# Customer journey mapping
journeys = df.groupby('customer_id')['action'].apply(list)
journey_patterns = {}

for journey in journeys:
    pattern = '->'.join(journey[-10:])  # Last 10 actions
    journey_patterns[pattern] = journey_patterns.get(pattern, 0) + 1

# Network analysis of product usage
G = nx.Graph()
for customer in df['customer_id'].unique():
    products = df[df['customer_id']==customer]['product'].unique()
    for i, p1 in enumerate(products):
        for p2 in products[i+1:]:
            G.add_edge(p1, p2)

# Community detection
communities = nx.community.louvain_communities(G)

# Statistical tests
churn_behavior = df[df['churned']==1]['usage_pattern']
retained_behavior = df[df['churned']==0]['usage_pattern']
stat, p_value = stats.mannwhitneyu(churn_behavior, retained_behavior)

print(f"Behavioral difference significance: p={p_value:.4f}")
print(f"Identified {len(communities)} product communities")
print(f"Top churn journey pattern: {max(journey_patterns, key=journey_patterns.get)}")`,
        score: 4.1
      },
      position: { x: 750, y: 700 },
    },
    
    // Evolution Process
    {
      id: 'evolution-1',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: 'Combining statistical rigor with business insights',
        scores: {
          'Depth': '4.3/5',
          'Rigor': '4.1/5',
          'Actionability': '3.9/5'
        }
      },
      position: { x: 400, y: 950 },
    },
    
    // Generation 2
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: Comprehensive Research' },
      position: { x: 400, y: 1100 },
      style: { ...themeStyles.metricNode, width: 280 },
    },
    {
      id: 'gen2-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'analysis_gen2_integrated.py',
        code: `import pandas as pd
import numpy as np
from sklearn.ensemble import GradientBoostingClassifier
from causalml.inference.tree import UpliftTreeClassifier
import matplotlib.pyplot as plt

class ChurnAnalyzer:
    """Comprehensive churn analysis framework"""
    
    def __init__(self, df):
        self.df = df
        self.segments = None
        self.model = None
        self.insights = []
    
    def run_full_analysis(self):
        # 1. Causal inference for intervention effects
        uplift = UpliftTreeClassifier(max_depth=5)
        X = self.df[self.features]
        treatment = self.df['received_intervention']
        outcome = self.df['churned']
        
        uplift.fit(X, treatment, outcome)
        self.df['uplift_score'] = uplift.predict(X)
        
        # 2. Multi-dimensional segmentation
        self.segments = self.advanced_segmentation()
        
        # 3. Temporal patterns
        temporal_insights = self.analyze_temporal_patterns()
        
        # 4. Generate actionable recommendations
        recommendations = self.generate_recommendations()
        
        # 5. Create executive summary
        self.create_executive_summary(recommendations)
        
        return self.compile_results()
    
    def generate_recommendations(self):
        """AI-generated actionable insights"""
        recs = []
        
        # High-impact interventions
        high_uplift = self.df[self.df['uplift_score'] > 0.2]
        recs.append({
            'action': 'Target retention campaigns',
            'segment': high_uplift['segment'].mode()[0],
            'expected_impact': f"{high_uplift['uplift_score'].mean():.1%} churn reduction",
            'priority': 'HIGH'
        })
        
        # Early warning system
        risk_indicators = self.identify_early_warnings()
        recs.append({
            'action': 'Implement early warning system',
            'triggers': risk_indicators,
            'lead_time': '30 days before churn',
            'priority': 'MEDIUM'
        })
        
        return recs`,
        score: 4.7
      },
      position: { x: 200, y: 1170 },
    },
    {
      id: 'gen2-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'analysis_gen2_report.py',
        code: `from reportlab.lib import colors
from reportlab.platypus import SimpleDocTemplate, Table, Paragraph
import plotly.graph_objects as go
from transformers import pipeline

class ResearchReportGenerator:
    """Auto-generate comprehensive research reports"""
    
    def __init__(self, analyzer_results):
        self.results = analyzer_results
        self.summarizer = pipeline("summarization", model="facebook/bart-large-cnn")
        
    def generate_full_report(self):
        report_sections = []
        
        # 1. Executive Summary with AI insights
        exec_summary = self.generate_executive_summary()
        report_sections.append(exec_summary)
        
        # 2. Key Findings with visualizations
        findings = self.compile_key_findings()
        
        # 3. Statistical Analysis
        stats_section = self.create_statistical_section()
        
        # 4. Predictive Models Performance
        model_section = self.evaluate_models()
        
        # 5. Recommendations with ROI
        recommendations = self.prioritize_recommendations()
        
        # 6. Interactive dashboards
        dashboard_config = self.create_dashboard_config()
        
        # Generate PDF report
        pdf = self.compile_pdf(report_sections)
        
        # Generate interactive HTML
        html = self.create_interactive_html(dashboard_config)
        
        return {
            'pdf_report': pdf,
            'interactive_html': html,
            'key_insights': findings[:5],
            'roi_estimate': self.calculate_roi(recommendations)
        }
    
    def generate_executive_summary(self):
        """AI-powered summary generation"""
        full_text = self.compile_analysis_text()
        summary = self.summarizer(full_text, max_length=130, min_length=30)
        
        return {
            'summary': summary[0]['summary_text'],
            'key_metrics': self.extract_key_metrics(),
            'risk_score': self.calculate_overall_risk()
        }`,
        score: 4.9
      },
      position: { x: 600, y: 1170 },
    },
    
    // Final Evolution
    {
      id: 'final-evolution',
      type: 'evolution',
      data: { 
        generation: 'Final',
        description: 'Publication-quality research with actionable insights',
        scores: {
          'Insight Depth': '4.9/5',
          'Statistical Rigor': '4.8/5',
          'Actionability': '4.9/5',
          'Comprehensiveness': '4.8/5',
          'Clarity': '4.7/5'
        }
      },
      position: { x: 400, y: 1400 },
    },
    
    // Future Vision
    {
      id: 'future-vision',
      data: { label: '🚀 Future: Multi-File Research Framework' },
      position: { x: 850, y: 1550 },
      style: { ...themeStyles.uniqueFeatureNode, width: 280 },
    },
    {
      id: 'multi-file-research',
      data: { label: '📁 Research Architecture\n\n• main_analysis.py\n• data_preprocessing.py\n• statistical_models.py\n• causal_inference.py\n• report_generator.py\n• visualization_suite.py\n• api_server.py' },
      position: { x: 850, y: 1650 },
      style: { ...themeStyles.suggestionNode, width: 220, height: 170 },
    },
    
    // Output
    {
      id: 'output',
      data: { label: '📊 Research Output\n• Interactive Report\n• API Endpoints\n• Real-time Dashboard\n• Actionable Insights' },
      position: { x: 400, y: 1600 },
      style: { ...themeStyles.uniqueFeatureNode, width: 200 },
    }
  ];
};

const researcherEdges = [
  // Initial flow
  { id: 'e1', source: 'research-question', target: 'data-context', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'data-context', target: 'initial-analysis', style: themeStyles.edgeStyle },
  { id: 'e3', source: 'initial-analysis', target: 'research-scoring', animated: true, style: themeStyles.edgeStyle },
  
  // Scoring connections
  { id: 'e4', source: 'research-scoring', target: 'insight-depth', style: themeStyles.edgeStyle },
  { id: 'e5', source: 'research-scoring', target: 'statistical-rigor', style: themeStyles.edgeStyle },
  { id: 'e6', source: 'research-scoring', target: 'actionability', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'research-scoring', target: 'comprehensiveness', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'research-scoring', target: 'clarity', style: themeStyles.edgeStyle },
  
  // Research Agents
  { id: 'e9', source: 'initial-analysis', target: 'research-agents', style: themeStyles.edgeStyle },
  { id: 'e10', source: 'research-agents', target: 'statistician', style: themeStyles.edgeStyle },
  { id: 'e11', source: 'research-agents', target: 'data-scientist', style: themeStyles.edgeStyle },
  { id: 'e12', source: 'research-agents', target: 'domain-expert', style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e13', source: 'statistician', target: 'gen1-1', style: themeStyles.edgeStyle },
  { id: 'e14', source: 'data-scientist', target: 'gen1-2', style: themeStyles.edgeStyle },
  { id: 'e15', source: 'domain-expert', target: 'gen1-3', style: themeStyles.edgeStyle },
  
  // Evolution
  { id: 'e16', source: 'gen1-1', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e17', source: 'gen1-2', target: 'evolution-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e18', source: 'gen1-3', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  
  // Feedback
  { id: 'f1', source: 'statistical-rigor', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f2', source: 'actionability', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  
  // Generation 2
  { id: 'e19', source: 'evolution-1', target: 'gen2-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e20', source: 'evolution-1', target: 'gen2-2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Final
  { id: 'e21', source: 'gen2-1', target: 'final-evolution', style: themeStyles.edgeStyle },
  { id: 'e22', source: 'gen2-2', target: 'final-evolution', animated: true, style: themeStyles.edgeStyle },
  { id: 'e23', source: 'final-evolution', target: 'output', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Future
  { id: 'e24', source: 'output', target: 'future-vision', style: { ...themeStyles.edgeStyle, strokeDasharray: '5 5' } },
  { id: 'e25', source: 'future-vision', target: 'multi-file-research', style: themeStyles.edgeStyle },
];

const ArtemisAutoResearcher = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(researcherEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisAutoResearcherPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisAutoResearcherPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #FFF0F5 0%, #FFE4E1 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#8B0000', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          🔬 Artemis Auto Researcher - Evolving Data Science
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(139, 0, 0, 0.08)',
          borderRadius: '20px',
          color: '#CD5C5C',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          From basic analysis to comprehensive research - watch insights evolve and deepen
        </div>

        {/* Reset button */}
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#DC143C',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            zIndex: 100,
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#B22222'}
          onMouseLeave={(e) => e.target.style.background = '#DC143C'}
        >
          Reset Layout
        </button>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              minZoom: 0.3,
              maxZoom: 1.0
            }}
            minZoom={0.2}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.4 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(255,255,255,0.8)',
                border: '1px solid #FFE4E1'
              }}
              nodeColor={(node) => {
                return node.style?.background || '#DC143C';
              }}
              nodeStrokeColor={() => '#8B0000'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="#FFE4E1" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Research Evolution Metrics */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          background: 'rgba(255,255,255,0.95)',
          padding: '15px',
          borderRadius: '15px',
          border: '1px solid #FFE4E1',
          color: '#8B0000',
          fontSize: '13px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          maxWidth: '350px'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: '600', color: '#DC143C' }}>
            📊 Research Quality Evolution
          </div>
          <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
            <div>• <strong>Insight Depth:</strong> Basic correlations → Causal inference</div>
            <div>• <strong>Methods:</strong> Descriptive stats → ML + Statistical rigor</div>
            <div>• <strong>Output:</strong> Simple prints → Interactive reports + APIs</div>
            <div>• <strong>Impact:</strong> Observations → Actionable recommendations</div>
          </div>
          <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(220, 20, 60, 0.1)', borderRadius: '8px' }}>
            <strong>Research Score:</strong> 2.3 → 4.9 
            <span style={{ color: '#28A745', fontWeight: '600' }}> (+113%)</span>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisAutoResearcher; 